import {
	faCheckCircle,
	faDownload,
	faEye,
	faShip,
	faTrash,
	faUserGear,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import JSZip from 'jszip';
import { PDFDocument } from 'pdf-lib';
import { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { tss } from 'tss-react';

import * as apiVesselPages from 'api/report/Getv1vesselPages';
import * as apiMoveValidated from 'api/report/Postv1filesmoveValidatedV1';
import Modal from 'components/desktop/_general/Modal/Modal';
import 'containers/desktop/Reports/ReportsTable.scss';
import * as Report from 'containers/desktop/Reports/types/Report';
import * as ReportGroup from 'containers/desktop/Reports/types/ReportGroup';
import * as ReportGroupList from 'containers/desktop/Reports/types/ReportGroupList';
import { EAlertType } from 'enums/alertType';
import { fireAlert } from 'functions/fireAlert';
import useTranslation from 'hooks/useTranslation';
import { useUser } from 'hooks/useUser';
import { RootState } from 'reducers/rootReducer';

interface IProps {
	reportGroups: ReportGroupList.Type;
	setReportGroups: (reportGroups: ReportGroupList.Type) => void;
	onRefresh: () => Promise<void>;
	disabled?: boolean;
}

const ReportsTable = ({ reportGroups, setReportGroups, onRefresh, disabled = false }: IProps) => {
	const { classes, cx } = useStyles();
	const { t } = useTranslation();
	const reduxCultureCode: string = useSelector((state: RootState) => state.cultureCode);
	const [downloadModal, setDownloadModal] = useState<{
		open: boolean;
		report: Report.Type | null;
	}>({ open: false, report: null });
	const [selectedVessel, setSelectedVessel] = useState<string>('all');
	const [vesselOptions, setVesselOptions] = useState<
		{ value: string; label: string; page?: number }[]
	>([{ value: 'all', label: 'All Vessels' }]);
	const [vesselLoading, setVesselLoading] = useState(false);
	const [vesselError, setVesselError] = useState<string | null>(null);
	const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
	const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
	const [pdfPreviewError, setPdfPreviewError] = useState<string | null>(null);
	const [exportLoading, setExportLoading] = useState<string | null>(null);
	const [validateLoading, setValidateLoading] = useState<string | null>(null);
	const { isSystemDeveloper, loading } = useUser();

	const handleSelectReport = (reportGroup: ReportGroup.Type, report: Report.Type) => {
		if (disabled || report.status !== 'done') {
			return;
		}
		setReportGroups(
			ReportGroupList.toggleSelectOfReport(reportGroups, reportGroup.id, report.id)
		);
	};

	const getStatusBadge = (status: Report.Type['status']) => {
		if (status === 'done') {
			return <span className="badge bg-success">Available</span>;
		}
		if (status === 'processing') {
			return (
				<span
					className="badge bg-primary position-relative"
					style={{
						overflow: 'hidden',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							background:
								'linear-gradient(120deg, rgba(255,255,255,0.3) 0%, #6066c1 50%, rgba(255,255,255,0.3) 100%)',
							backgroundSize: '200% 100%',
							animation: ' shimmer 2s infinite',
						}}
					/>
					<span style={{ position: 'relative', zIndex: 1 }}>Processing</span>
				</span>
			);
		}
		if (status === 'validate') {
			return <span className="badge bg-primary">Waiting for validation</span>;
		}
		return <span className="badge bg-danger">Failed</span>;
	};

	const openDownloadModal = (report: Report.Type) => {
		setSelectedVessel('all');
		setDownloadModal({ open: true, report });
		setPdfPreviewUrl(null);
		setPdfPreviewError(null);
		loadVessels(report.id);
		loadPdfPreview(report.id);
	};

	const closeDownloadModal = () => {
		if (pdfPreviewUrl) {
			URL.revokeObjectURL(pdfPreviewUrl);
			setPdfPreviewUrl(null);
		}
		setDownloadModal({ open: false, report: null });
		setPdfPreviewError(null);
	};

	const generateFileName = (
		report: Report.Type,
		type: 'full' | 'fleet' | 'vessel',
		vesselName?: string
	): string => {
		const dateStart = report.dateStart;
		let year = '';
		let month = '';

		if (dateStart && dateStart.includes('-')) {
			const parts = dateStart.split('-');
			if (parts.length >= 2) {
				year = parts[0] || '';
				month = (parts[1] || '').padStart(2, '0');
			}
		}

		if (!year || !month) {
			const now = new Date();
			year = now.getFullYear().toString();
			month = (now.getMonth() + 1).toString().padStart(2, '0');
		}

		const safeGroupName =
			(report.groupName || 'unknown')
				.replace(/[<>:"/\\|?*]/g, '_')
				.replace(/\s+/g, '_')
				.replace(/_{2,}/g, '_')
				.replace(/^_+|_+$/g, '')
				.trim() || 'unknown';

		const baseName = `${year}-${month}-${safeGroupName}`;

		if (type === 'full') {
			return `${baseName}.pdf`;
		} else if (type === 'fleet') {
			return `${baseName}-fleet.pdf`;
		} else if (type === 'vessel' && vesselName) {
			const safeVesselName =
				vesselName
					.replace(/[<>:"/\\|?*]/g, '_')
					.replace(/\s+/g, '_')
					.replace(/_{2,}/g, '_')
					.replace(/^_+|_+$/g, '')
					.trim() || 'vessel';
			return `${baseName}-${safeVesselName}.pdf`;
		}

		return `${report.id.replace(/[^a-zA-Z0-9-_]/g, '_')}.pdf`;
	};

	const handleDownloadCurrent = async () => {
		if (!downloadModal.report) return;
		const zipBlob = await ReportGroupList.downloadSingleBlob(downloadModal.report.id);
		if (!zipBlob) return;

		const pdfArrayBuffer = await extractPdfFromZip(zipBlob);
		if (!pdfArrayBuffer) {
			alert('No PDF found in the downloaded archive.');
			return;
		}

		try {
			if (selectedVessel === 'all') {
				const pdfBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
				const fileURL = URL.createObjectURL(pdfBlob);
				const link = document.createElement('a');
				link.href = fileURL;
				link.download = generateFileName(downloadModal.report, 'full');
				link.click();
				URL.revokeObjectURL(fileURL);
				return;
			}

			const vesselOption = vesselOptions.find((v) => v.value === selectedVessel);
			const targetPageNumber = vesselOption?.page ?? NaN;
			const vesselName = vesselOption?.label;

			if (!targetPageNumber || Number.isNaN(targetPageNumber)) {
				alert('Selected vessel has no page index.');
				return;
			}

			const pdfDoc = await PDFDocument.load(pdfArrayBuffer);
			const pageCount = pdfDoc.getPageCount();

			if (targetPageNumber < 1 || targetPageNumber > pageCount) {
				console.error('Requested page out of range', {
					targetPageNumber,
					pageCount,
				});
				alert(`Requested page ${targetPageNumber} is out of range (1-${pageCount}).`);
				return;
			}

			const targetIndex = targetPageNumber - 1;
			const newPdf = await PDFDocument.create();
			const [copiedPage] = await newPdf.copyPages(pdfDoc, [targetIndex]);
			newPdf.addPage(copiedPage);
			const newPdfBytes = await newPdf.save();
			const singlePageBlob = new Blob([newPdfBytes as BlobPart], { type: 'application/pdf' });

			const fileURL = URL.createObjectURL(singlePageBlob);
			const link = document.createElement('a');
			link.href = fileURL;

			if (targetPageNumber === 1 || selectedVessel === 'report-overview') {
				link.download = generateFileName(downloadModal.report, 'fleet');
			} else {
				link.download = generateFileName(
					downloadModal.report,
					'vessel',
					vesselName || `vessel-${targetPageNumber}`
				);
			}

			link.click();
			URL.revokeObjectURL(fileURL);
		} catch (error) {
			console.error('Error extracting PDF page', error);
			alert('Could not extract the requested page from the PDF.');
		}
	};

	const loadVessels = async (fileId: string) => {
		setVesselLoading(true);
		setVesselError(null);
		try {
			const data = await apiVesselPages.callApi(fileId);
			const mapped = data.map((v, idx) => ({
				value: `vessel-${idx + 1}`,
				label: v.page_name,
				page: v.page_index,
			}));

			const pageOneIndex = mapped.findIndex((v) => v.page === 1);
			if (pageOneIndex !== -1) {
				mapped[pageOneIndex] = {
					...mapped[pageOneIndex],
					value: 'report-overview',
					label: 'Report Overview',
				};
			} else {
				mapped.unshift({ value: 'report-overview', label: 'Report Overview', page: 1 });
			}

			setVesselOptions([{ value: 'all', label: 'All Vessels' }, ...mapped]);
		} catch (error) {
			console.error('Failed to load vessel pages', error);
			setVesselError('Could not load vessel pages.');
			setVesselOptions([
				{ value: 'all', label: 'All Vessels' },
				{ value: 'report-overview', label: 'Report Overview', page: 1 },
			]);
		} finally {
			setVesselLoading(false);
		}
	};

	const extractPdfFromZip = async (zipBlob: Blob): Promise<ArrayBuffer | null> => {
		try {
			const zip = await JSZip.loadAsync(zipBlob);
			const pdfEntry = Object.values(zip.files).find(
				(f) => !f.dir && f.name.toLowerCase().endsWith('.pdf')
			);
			if (!pdfEntry) {
				return null;
			}
			return await pdfEntry.async('arraybuffer');
		} catch (error) {
			console.error('Error extracting PDF from zip', error);
			return null;
		}
	};

	const loadPdfPreview = async (reportId: string) => {
		setPdfPreviewLoading(true);
		setPdfPreviewError(null);

		if (pdfPreviewUrl) {
			URL.revokeObjectURL(pdfPreviewUrl);
			setPdfPreviewUrl(null);
		}

		try {
			const zipBlob = await ReportGroupList.downloadSingleBlob(reportId);
			if (!zipBlob) {
				setPdfPreviewError('Could not download the report file.');
				setPdfPreviewLoading(false);
				return;
			}

			const pdfArrayBuffer = await extractPdfFromZip(zipBlob);
			if (!pdfArrayBuffer) {
				setPdfPreviewError('No PDF found in the downloaded archive.');
				setPdfPreviewLoading(false);
				return;
			}

			const pdfBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
			const previewUrl = URL.createObjectURL(pdfBlob);
			setPdfPreviewUrl(previewUrl);
		} catch (error) {
			console.error('Error loading PDF preview', error);
			setPdfPreviewError('Could not load PDF preview.');
		} finally {
			setPdfPreviewLoading(false);
		}
	};

	const handleDeleteReport = async (report: Report.Type) => {
		await ReportGroupList.deleteSingle(report.id);
		await onRefresh();
	};

	const handleValidate = async (report: Report.Type) => {
		setValidateLoading(report.id);
		try {
			await apiMoveValidated.callApi({
				requestBody: {
					fileIds: [report.id],
				},
			});
			// Show success notification
			await fireAlert(
				EAlertType.success,
				t('admin:REPORTS'),
				'Report has been successfully validated.'
			);
			// Refresh the reports list after successful validation
			await onRefresh();
		} catch (error) {
			console.error('Error validating report', error);
			// Show failure notification
			await fireAlert(
				EAlertType.warning,
				t('admin:REPORTS'),
				'Failed to validate report. Please try again.'
			);
		} finally {
			setValidateLoading(null);
		}
	};

	const handleExportAllData = async (report: Report.Type) => {
		if (report.status !== 'done') {
			return;
		}

		setExportLoading(report.id);
		try {
			const zipBlob = await ReportGroupList.downloadSingleBlob(report.id);
			if (!zipBlob) {
				alert('Could not download the report file.');
				setExportLoading(null);
				return;
			}

			const pdfArrayBuffer = await extractPdfFromZip(zipBlob);
			if (!pdfArrayBuffer) {
				alert('No PDF found in the downloaded archive.');
				setExportLoading(null);
				return;
			}

			const pdfDoc = await PDFDocument.load(pdfArrayBuffer);
			const pageCount = pdfDoc.getPageCount();

			const exportZip = new JSZip();

			const fullPdfBytes = await pdfDoc.save();
			const fullFileName = generateFileName(report, 'full');
			exportZip.file(fullFileName, fullPdfBytes);

			if (pageCount >= 1) {
				const overviewPdf = await PDFDocument.create();
				const [overviewPage] = await overviewPdf.copyPages(pdfDoc, [0]);
				overviewPdf.addPage(overviewPage);
				const overviewPdfBytes = await overviewPdf.save();
				const overviewFileName = generateFileName(report, 'fleet');
				exportZip.file(overviewFileName, overviewPdfBytes);
			}

			try {
				const vesselData = await apiVesselPages.callApi(report.id);

				for (const vessel of vesselData) {
					const pageIndex = vessel.page_index - 1;

					if (pageIndex === 0) {
						continue;
					}

					if (pageIndex >= 0 && pageIndex < pageCount) {
						const vesselPdf = await PDFDocument.create();
						const [vesselPage] = await vesselPdf.copyPages(pdfDoc, [pageIndex]);
						vesselPdf.addPage(vesselPage);
						const vesselPdfBytes = await vesselPdf.save();
						const vesselFileName = generateFileName(report, 'vessel', vessel.page_name);
						exportZip.file(vesselFileName, vesselPdfBytes);
					}
				}
			} catch (error) {
				console.error('Error loading vessel pages for export:', error);
			}

			const exportZipBlob = await exportZip.generateAsync({ type: 'blob' });
			const fileURL = URL.createObjectURL(exportZipBlob);
			const link = document.createElement('a');
			link.href = fileURL;

			const dateStart = report.dateStart;
			let year = '';
			let month = '';
			if (dateStart && dateStart.includes('-')) {
				const parts = dateStart.split('-');
				if (parts.length >= 2) {
					year = parts[0] || '';
					month = (parts[1] || '').padStart(2, '0');
				}
			}
			if (!year || !month) {
				const now = new Date();
				year = now.getFullYear().toString();
				month = (now.getMonth() + 1).toString().padStart(2, '0');
			}
			const safeGroupName =
				(report.groupName || 'unknown')
					.replace(/[<>:"/\\|?*]/g, '_')
					.replace(/\s+/g, '_')
					.replace(/_{2,}/g, '_')
					.replace(/^_+|_+$/g, '')
					.trim() || 'unknown';

			link.download = `${year}-${month}-${safeGroupName}-export.zip`;
			link.click();
			URL.revokeObjectURL(fileURL);
		} catch (error) {
			console.error('Error exporting all data:', error);
			alert('Could not export all data. Please try again.');
		} finally {
			setExportLoading(null);
		}
	};

	function formatLocalDate(date: Date, fullDateTime: boolean): string {
		const adjustedDate = new Date(date.getTime() + 1 * 60 * 60 * 1000);

		if (fullDateTime)
			return adjustedDate.toLocaleString(reduxCultureCode, {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit',
			});

		return adjustedDate.toLocaleString(reduxCultureCode, {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		});
	}

	function formatReportPeriod(dateStart: string): string {
		if (!dateStart || !dateStart.includes('-')) {
			return '';
		}
		const [year, month] = dateStart.split('-');
		const monthNum = parseInt(month, 10);
		if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
			return dateStart;
		}
		const date = new Date(parseInt(year, 10), monthNum - 1, 1);
		return date.toLocaleString(reduxCultureCode, { month: 'long', year: 'numeric' });
	}

	return (
		<div className={classes.container}>
			<table
				className={cx(
					'table table-hover table-bordered align-middle table-sm',
					classes.table
				)}
			>
				<thead className="table-light">
					<tr>
						<th>Group Name</th>
						<th>{t('admin:UPLOAD_DATE')}</th>
						<th>Report Period</th>
						<th className={classes.labelCol}>{t('admin:STATUS')}</th>
						<th>{t('admin:USERNAME')}</th>
						<th className={classes.actionsCol}>{'Actions'}</th>
					</tr>
				</thead>
				<tbody>
					{reportGroups.flatMap((reportGroup: ReportGroup.Type) =>
						reportGroup.reports.map((report: Report.Type, reportKey: number) => (
							<tr
								key={`${reportGroup.id}-${report.id}-${reportKey}`}
								className={cx(
									report.isSelected ? 'table-primary' : '',
									report.status === 'done'
										? classes.mousePointer
										: classes.disabledRow,
									classes.hoverRow
								)}
								onClick={() => {
									if (!disabled) {
										handleSelectReport(reportGroup, report);
									}
								}}
								style={
									disabled
										? { cursor: 'not-allowed', opacity: 0.6 }
										: { cursor: 'pointer' }
								}
							>
								<td>{report.groupName || '-'}</td>
								<td>{formatLocalDate(report.uploadedAt, true)}</td>
								<td>{formatReportPeriod(report.dateStart)}</td>
								<td className={classes.labelCol}>
									{getStatusBadge(report.status)}
								</td>
								<td className={classes.author}>{report.username}</td>
								<td className={classes.actionsCol}>
									<div className="d-flex gap-1 justify-content-center">
										{report.status === 'validate' && isSystemDeveloper ? (
											<button
												className="btn btn-sm btn-outline-primary"
												onClick={(e) => {
													e.stopPropagation();
													handleValidate(report);
												}}
												disabled={disabled || validateLoading === report.id}
												title="Validate report"
											>
												{validateLoading === report.id ? (
													<>
														<Spinner size="sm" animation="border" className="me-1" />
														Validating...
													</>
												) : (
													<>
														<FontAwesomeIcon
															icon={faUserGear}
															className="me-1"
														/>
														Validate
													</>
												)}
											</button>
										) : (
											<>
												<button
													className="btn btn-sm btn-outline-primary"
													onClick={(e) => {
														e.stopPropagation();
														openDownloadModal(report);
													}}
													disabled={disabled || report.status !== 'done'}
													title="Preview and Download"
												>
													<FontAwesomeIcon
														icon={faEye}
														className="me-1"
													/>
													Preview
												</button>
												<button
													className="btn btn-sm btn-outline-success"
													onClick={(e) => {
														e.stopPropagation();
														handleExportAllData(report);
													}}
													disabled={
														disabled ||
														report.status !== 'done' ||
														exportLoading === report.id
													}
													title="Export All Data"
												>
													{exportLoading === report.id ? (
														<>
															<Spinner
																size="sm"
																animation="border"
																className="me-1"
															/>
															Download All
														</>
													) : (
														<>
															<FontAwesomeIcon
																icon={faDownload}
																className="me-1"
															/>
															Download All PDFs
														</>
													)}
												</button>
											</>
										)}
										{/* <button
											className="btn btn-sm btn-outline-danger"
											onClick={async (e) => {
												e.stopPropagation();
												await handleDeleteReport(report);
											}}
											title={t('admin:DELETE') ?? 'Delete'}
											disabled={report.status == 'processing'}
										>
											<FontAwesomeIcon icon={faTrash} />
										</button> */}
									</div>
								</td>
							</tr>
						))
					)}
				</tbody>
			</table>
			<Modal
				headerIcon={<FontAwesomeIcon icon={faDownload} />}
				headerText={downloadModal.report?.id ?? 'Download report data'}
				bodyContent={
					<>
						<div className="mb-4">
							{pdfPreviewLoading && (
								<div className="text-center py-4">
									<div className="spinner-border text-primary" role="status">
										<span className="visually-hidden">
											Loading PDF preview...
										</span>
									</div>
									<p className="mt-2 text-muted small">Loading PDF preview...</p>
								</div>
							)}
							{pdfPreviewError && (
								<div className="alert alert-warning" role="alert">
									<small>{pdfPreviewError}</small>
								</div>
							)}
							{pdfPreviewUrl && !pdfPreviewLoading && (
								<div className="border rounded p-2 bg-light">
									<p className="small text-muted mb-2">PDF Preview</p>
									<iframe
										src={pdfPreviewUrl}
										style={{
											width: '100%',
											height: '600px',
											border: '1px solid #dee2e6',
											borderRadius: '4px',
										}}
										title="PDF Preview"
									/>
								</div>
							)}
						</div>

						<div className="d-flex justify-content-between align-items-center gap-2">
							<div className="d-flex align-items-center gap-2 flex-grow-1">
								<FontAwesomeIcon icon={faShip} />
								<select
									className="form-select form-select-sm"
									value={selectedVessel}
									onChange={(e) => setSelectedVessel(e.target.value)}
									disabled={vesselLoading}
								>
									{vesselOptions.map((v) => (
										<option key={v.value} value={v.value}>
											{v.label}
										</option>
									))}
								</select>
							</div>
							<button
								className="btn btn-primary"
								onClick={handleDownloadCurrent}
								disabled={vesselLoading}
							>
								{t('admin:DOWNLOAD') ?? 'Download'}
							</button>
						</div>
						{vesselError && <div className="text-danger small mt-2">{vesselError}</div>}
					</>
				}
				isOpen={downloadModal.open}
				close={closeDownloadModal}
				size="xl"
			/>
		</div>
	);
};

export default ReportsTable;

const useStyles = tss.create({
	container: {
		maxHeight: '60vh',
		overflowY: 'auto',
	},
	table: {
		fontSize: '0.8em',
	},
	author: {},
	checkboxCol: {
		textAlign: 'center',
		width: '24px',
		whiteSpace: 'nowrap',
	},
	labelCol: {
		textAlign: 'center',
	},
	mousePointer: {
		cursor: 'pointer',
	},
	disabledRow: {
		cursor: 'not-allowed',
	},
	hoverRow: {
		transition: 'background-color 0.1s ease',
		':hover': {
			backgroundColor: '#f5f5f5',
		},
	},
	actionsCol: {
		textAlign: 'center',
		whiteSpace: 'nowrap',
		width: '120px',
	},
});
